import React, { useEffect, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { BrandLogo } from './components/BrandLogo';
import { HeroSection } from './components/HeroSection';
import { PropertyDetails } from './components/PropertyDetails';
import { GallerySection } from './components/GallerySection';
import { ContactSection } from './components/ContactSection';
// ─── Tokens ────────────────────────────────────────────────────────────────
const BRAND_BLUE = '#D6E8F5';
const BRAND_DARK = '#1a1714';
const WARM_WHITE = '#f9f6f2';
const WARM_STONE = '#ede8e1';
const TAUPE = '#7a7268';
const BRASS = '#a8885a';
const DIVIDER = 'rgba(26,23,20,0.10)';
// ─── Nav items ─────────────────────────────────────────────────────────────
const NAV_LINKS = [{
  label: 'Residence',
  href: '#details'
}, {
  label: 'Gallery',
  href: '#gallery'
}, {
  label: 'Enquire',
  href: '#enquire'
}];
export function App() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const {
    scrollY
  } = useScroll();
  // Parallax opacity for the thin brass rule under the hero
  const ruleOpacity = useTransform(scrollY, [0, 200], [0, 1]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);
  return <div className="min-h-screen font-sans antialiased" style={{
    background: WARM_WHITE,
    color: BRAND_DARK
  }} data-id="element-0">
      {/* ── Fixed Header ─────────────────────────────────────────────────── */}
      <motion.header initial={{
      opacity: 0,
      y: -8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 1.4,
      delay: 0.2,
      ease: [0.22, 1, 0.36, 1]
    }} style={{
      backgroundColor: scrolled ? 'rgba(249,246,242,0.96)' : 'transparent',
      borderBottomColor: scrolled ? DIVIDER : 'transparent',
      backdropFilter: scrolled ? 'blur(16px)' : 'none',
      WebkitBackdropFilter: scrolled ? 'blur(16px)' : 'none'
    }} className="fixed top-0 left-0 right-0 z-50 transition-all duration-700 ease-out border-b" data-id="element-1">
        <div className="mx-auto flex items-center justify-between" style={{
        maxWidth: '1400px',
        padding: scrolled ? '1rem 3rem' : '1.75rem 3rem',
        transition: 'padding 0.7s ease'
      }} data-id="element-2">
          {/* Desktop nav — left */}
          <nav className="hidden md:flex items-center gap-10 flex-1" data-id="element-3">
            {NAV_LINKS.slice(0, 2).map(({
            label,
            href
          }) => <a key={label} href={href} style={{
            color: scrolled ? TAUPE : 'rgba(255,255,255,0.75)',
            fontSize: '11px',
            letterSpacing: '0.22em',
            fontWeight: 500
          }} className="uppercase transition-colors duration-400 hover:opacity-100" onMouseEnter={e => {
            ;
            (e.target as HTMLAnchorElement).style.color = scrolled ? BRAND_DARK : '#fff';
          }} onMouseLeave={e => {
            ;
            (e.target as HTMLAnchorElement).style.color = scrolled ? TAUPE : 'rgba(255,255,255,0.75)';
          }} data-id="element-4">
                {label}
              </a>)}
          </nav>

          {/* Logo — centre */}
          <div className="flex-1 flex justify-center" data-id="element-5">
            <BrandLogo style={{
            height: scrolled ? '14px' : '20px',
            width: 'auto',
            transition: 'height 0.7s ease'
          }} fill={scrolled ? BRAND_DARK : '#ffffff'} data-id="element-6" />
          </div>

          {/* CTA — right */}
          <div className="hidden md:flex flex-1 justify-end items-center gap-8" data-id="element-7">
            <a href="#enquire" style={{
            fontSize: '10px',
            letterSpacing: '0.22em',
            fontWeight: 600,
            padding: '0.6rem 1.4rem',
            border: `1px solid ${scrolled ? BRASS : 'rgba(255,255,255,0.5)'}`,
            color: scrolled ? BRASS : '#fff',
            transition: 'all 0.4s ease'
          }} className="uppercase tracking-widest" onMouseEnter={e => {
            const el = e.currentTarget;
            el.style.background = BRASS;
            el.style.borderColor = BRASS;
            el.style.color = '#fff';
          }} onMouseLeave={e => {
            const el = e.currentTarget;
            el.style.background = 'transparent';
            el.style.borderColor = scrolled ? BRASS : 'rgba(255,255,255,0.5)';
            el.style.color = scrolled ? BRASS : '#fff';
          }} data-id="element-8">
              Enquire
            </a>
          </div>

          {/* Mobile hamburger */}
          <button className="md:hidden flex-1 flex justify-end" onClick={() => setMenuOpen(v => !v)} aria-label="Toggle menu" style={{
          color: scrolled ? BRAND_DARK : '#fff'
        }} data-id="element-9">
            
          </button>
        </div>

        {/* Thin brass rule that fades in on scroll */}
        <motion.div style={{
        height: '1px',
        background: `linear-gradient(to right, transparent, ${BRASS}, transparent)`,
        opacity: ruleOpacity
      }} data-id="element-10" />
      </motion.header>

      {/* ── Mobile Menu Overlay ──────────────────────────────────────────── */}
      {menuOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-40 flex flex-col items-center justify-center gap-10" style={{
      background: WARM_WHITE
    }} data-id="element-11">
          {NAV_LINKS.map(({
        label,
        href
      }) => <a key={label} href={href} onClick={() => setMenuOpen(false)} style={{
        fontFamily: 'Georgia, serif',
        fontSize: 'clamp(2rem, 8vw, 3rem)',
        color: BRAND_DARK,
        letterSpacing: '0.05em'
      }} data-id="element-12">
              {label}
            </a>)}
        </motion.div>}

      {/* ── Main Content ─────────────────────────────────────────────────── */}
      <main data-id="element-13">
        {/* Hero — full bleed, dark overlay, components handle imagery */}
        <HeroSection data-id="element-14" />

        {/* ── Thin brass divider ─────────────────────────────────────────── */}
        <div style={{
        height: '1px',
        background: `linear-gradient(to right, transparent, ${BRASS} 30%, ${BRASS} 70%, transparent)`,
        opacity: 0.35
      }} data-id="element-15" />

        {/* ── Property details — warm white ─────────────────────────────── */}
        <section id="details" style={{
        background: WARM_WHITE
      }} data-id="element-16">
          <PropertyDetails data-id="element-17" />
        </section>

        {/* ── Brand blue band — address / key stats ─────────────────────── */}
        <div style={{
        background: BRAND_BLUE,
        borderTop: `1px solid rgba(168,136,90,0.18)`,
        borderBottom: `1px solid rgba(168,136,90,0.18)`,
        padding: '3rem',
        textAlign: 'center'
      }} data-id="element-18">
          <p style={{
          fontSize: '10px',
          letterSpacing: '0.3em',
          color: TAUPE,
          marginBottom: '0.5rem'
        }} className="uppercase" data-id="element-19">
            An Abercrombys Prestige Offering
          </p>
          <p style={{
          fontFamily: 'Georgia, serif',
          fontSize: 'clamp(1.1rem, 2vw, 1.5rem)',
          color: BRAND_DARK,
          letterSpacing: '0.06em'
        }} data-id="element-20">
            10 Union Street, Armadale
          </p>
          <p style={{
          fontSize: '10px',
          letterSpacing: '0.22em',
          color: TAUPE,
          marginTop: '0.75rem',
          fontStyle: 'italic',
          fontFamily: 'Georgia, serif'
        }} data-id="element-21">
            A residence of singular distinction
          </p>

          {/* Auction details */}
          <div style={{
          marginTop: '2rem',
          paddingTop: '1.5rem',
          borderTop: `1px solid rgba(168,136,90,0.15)`
        }} data-id="element-22">
            <p style={{
            fontSize: '10px',
            letterSpacing: '0.3em',
            color: TAUPE,
            marginBottom: '0.4rem'
          }} className="uppercase" data-id="element-23">
              Auction
            </p>
            <p style={{
            fontFamily: 'Georgia, serif',
            fontSize: 'clamp(0.9rem, 1.5vw, 1.15rem)',
            color: BRAND_DARK,
            letterSpacing: '0.04em'
          }} data-id="element-24">
              February 28th at 12:30pm
            </p>
          </div>
        </div>

        {/* ── Gallery — warm stone bg ───────────────────────────────────── */}
        <section id="gallery" style={{
        background: WARM_STONE
      }} data-id="element-25">
          <GallerySection data-id="element-26" />
        </section>

        {/* ── Contact — brand blue bg ───────────────────────────────────── */}
        <section id="enquire" style={{
        background: BRAND_BLUE,
        borderTop: `1px solid rgba(168,136,90,0.2)`
      }} data-id="element-27">
          <ContactSection data-id="element-28" />
        </section>
      </main>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <footer style={{
      background: BRAND_DARK,
      borderTop: `2px solid ${BRASS}`,
      padding: '3.5rem'
    }} data-id="element-29">
        <div className="mx-auto flex flex-col md:flex-row items-center justify-between gap-6" style={{
        maxWidth: '1400px'
      }} data-id="element-30">
          {/* Logo */}
          <BrandLogo style={{
          height: '14px',
          width: 'auto',
          opacity: 0.9
        }} fill="#ffffff" data-id="element-31" />

          {/* Centre — copyright */}
          <div className="flex flex-col items-center gap-1 text-center" data-id="element-32">
            <p style={{
            fontSize: '10px',
            letterSpacing: '0.22em',
            color: 'rgba(255,255,255,0.3)'
          }} className="uppercase" data-id="element-33">
              Abercrombys Real Estate © 2026
            </p>
          </div>

          {/* Right — enquire */}
          <div className="flex items-center" data-id="element-34"></div>
        </div>

        {/* Bottom rule */}
        <div style={{
        marginTop: '2.5rem',
        height: '1px',
        background: `linear-gradient(to right, transparent, ${BRASS}, transparent)`,
        opacity: 0.2
      }} data-id="element-35" />
        <p style={{
        textAlign: 'center',
        marginTop: '1.5rem',
        fontSize: '9px',
        letterSpacing: '0.18em',
        color: 'rgba(255,255,255,0.2)'
      }} className="uppercase" data-id="element-36">
          Licensed Estate Agent · Victoria · 10 Union Street, Armadale VIC 3143
        </p>
      </footer>
    </div>;
}